<template>
  <div id="app">
    <transition name="fade">
      <div v-if="content && user && !user.error">
        <img class="logo" src="./assets/logo.png" />
        <p class="intro-text">{{ content.intro_text | replaceName(user) }}</p>
        <section class="stats">
          <span class="stats__headline headline">
            {{ content.stats_headline }}
          </span>
          <div class="stats__wrapper">
            <div class="stats__category">
              <img class="stats__icon" src="./assets/icon-pin.svg" />
              <div class="stats__value">{{ user.tour_place }}</div>
              <div class="stats__label">{{ content.stats_location_label }}</div>
            </div>

            <div class="stats__category">
              <img class="stats__icon" src="./assets/icon-schedule.svg" />
              <div class="stats__value">
                {{ user.tour_start | formatDateTime }}
              </div>
              <div class="stats__label">{{ content.stats_date_label }}</div>
            </div>

            <div class="stats__category">
              <img class="stats__icon" src="./assets/icon-clock.svg" />
              <div class="stats__value">
                {{ getDuration(user.tour_start, user.tour_end) }}
                {{ content.stats_duration_unit }}
              </div>
              <div class="stats__label">{{ content.stats_duration_label }}</div>
            </div>

            <div class="stats__category">
              <img
                class="stats__icon"
                v-if="parseInt(user.rating_visit) >= 7"
                src="./assets/icon-smiley2.svg"
              />
              <img
                class="stats__icon"
                v-else-if="parseInt(user.rating_visit) >= 4"
                src="./assets/icon-smiley1.svg"
              />
              <img class="stats__icon" v-else src="./assets/icon-smiley0.svg" />
              <div class="stats__value">
                {{ user.rating_visit }} {{ content.stats_rating_unit }}
              </div>
              <div class="stats__label">{{ content.stats_rating_label }}</div>
            </div>
          </div>
        </section>
        <section class="highlight">
          <div class="highlight__headline headline">
            {{ content.highlights_headline }}
          </div>

          <div v-if="!user.interest">
            <img
              src="./assets/highlights_smarthome.png"
              alt
              class="highlight__image"
            />
            <div class="highlight__text-wrapper">
              <div class="highlight__detail-head">
                {{ content.highlights_smarthome_headline }}
                <div></div>
              </div>
              <div class="highlight__copy">
                {{ content.highlights_smarthome_copy }}
              </div>
              <download-link
                :file="content.highlights_smarthome_pdf"
                :label="content.download_label"
              ></download-link>
            </div>
          </div>

          <div v-else>
            <img
              src="./assets/highlights_industry.png"
              alt
              class="highlight__image"
            />
            <div class="highlight__text-wrapper">
              <div class="highlight__detail-head">
                {{ content.highlights_industry_headline }}
                <div></div>
              </div>
              <div class="highlight__copy">
                {{ content.highlights_industry_copy }}
              </div>
              <download-link
                :file="content.highlights_industry_pdf"
                :label="content.download_label"
              ></download-link>
            </div>
          </div>
        </section>

        <div class="further-topics">
          <h2 class="further-topics__headline headline">
            {{ content.topics_headline }}
          </h2>

          <div class="further-topics__wrapper">
            <div class="further-topics__topic" v-if="!user.interest">
              <img
                class="further-topics__icon"
                src="./assets/icon-techarm.svg"
                alt
              />
              <div class="further-topics__label">
                {{ content.topics_industry_label }}
              </div>
              <download-link
                :file="content.topics_industry_pdf"
                :label="content.download_label"
              ></download-link>
            </div>

            <div class="further-topics__topic" v-else>
              <img
                class="further-topics__icon"
                src="./assets/icon-smarthome.svg"
                alt
              />
              <div class="further-topics__label">
                {{ content.topics_smarthome_label }}
              </div>
              <download-link
                :file="content.topics_smarthome_pdf"
                :label="content.download_label"
              ></download-link>
            </div>

            <div class="further-topics__topic">
              <img class="further-topics__icon" src="./assets/logo.png" alt />
              <div class="further-topics__label">
                {{ content.topics_sonepar_label }}
              </div>
              <download-link
                :file="content.topics_sonepar_pdf"
                :label="content.download_label"
              ></download-link>
            </div>
          </div>
        </div>

        <section class="more-information">
          <div class="more-information__headline headline">
            {{ content.more_info_headline }}
          </div>
          <a :href="content.more_info_url" target="_blank">
            <img
              class="more-information__logo"
              src="./assets/Innovation_Lab_LOGO_weiss_cyan.png"
            />
          </a>
          <ul class="more-information__list">
            <li>
              <img
                class="more-information__icon"
                src="./assets/icon-globe.svg"
              />
              <div>
                <a
                  class="more-information__link"
                  :href="content.contact_website_href"
                  target="_blank"
                  >{{ content.contact_website_label }}</a
                >
              </div>
            </li>
            <li>
              <img
                class="more-information__icon"
                src="./assets/icon-instagram.svg"
              />
              <div>
                <a
                  class="more-information__link"
                  :href="content.contact_instagram_href"
                  target="_blank"
                  >{{ content.contact_instagram_label }}</a
                >
                <div class="more-information__copy">
                  {{ content.contact_instagram_copy }}
                </div>
              </div>
            </li>
            <li>
              <img
                class="more-information__icon"
                src="./assets/icon-facebook.svg"
              />
              <div>
                <a
                  class="more-information__link"
                  :href="content.contact_facebook_href"
                  target="_blank"
                  >{{ content.contact_facebook_label }}</a
                >
                <div class="more-information__copy">
                  {{ content.contact_facebook_copy }}
                </div>
              </div>
            </li>
          </ul>
        </section>

        <section class="contact">
          <div class="contact__headline headline">
            {{ content.contact_headline }}
          </div>
          <ul class="contact__list">
            <li>
              <img class="contact__icon" src="./assets/icon-letter.svg" />
              <div>
                <a
                  class="contact__link"
                  :href="'mailto:' + content.contact_email_href"
                  target="_blank"
                  >{{ content.contact_email_label }}</a
                >
              </div>
            </li>
          </ul>
        </section>

        <footer class="footer">
          <a :href="content.imprint_href" class="footer-link" target="_blank">{{
            content.imprint_label
          }}</a>
          <a :href="content.legal_href" class="footer-link" target="_blank">
            {{ content.legal_label }}
          </a>
        </footer>
      </div>

      <div v-else-if="user.error" class="notfound">
        <span class="notfound__headline">404</span>
        <span class="notfound__copy">Page not found</span>
      </div>
      <div v-else style="color: white">LOADING</div>
    </transition>

    <cookie-layer
      v-if="content"
      :copy="content.cookie_layer_copy"
      :btn-label="content.cookie_layer_btn_label"
    ></cookie-layer>
  </div>
</template>

<script>
import DownloadLink from "./components/DownloadLink.vue";
import CookieLayer from "./components/CookieLayer.vue";
import axios from "axios";

export default {
  name: "app",
  components: {
    DownloadLink,
    CookieLayer,
  },
  data() {
    return {
      content: null,
      filesUrl: process.env.VUE_APP_FILES_URL,
      user: window.user,
    };
  },
  mounted() {
    if (!this.user.error) {
      this.getCMSContent().then((translation) => {
        this.content = translation;
      });
    }
  },
  methods: {
    getCMSContent() {
      const language = this.user.language || "en";
      const options = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        mode: "no-cors",
      };

      return axios
        .get(
          `${process.env.VUE_APP_API_URL}/landingpage?lang=${language}&fields=translations.*.*&single=1`,
          options
        )
        .then((response) => {
          return response.data.data.translations[0];
        })
        .catch((err) => console.log(err));
    },
    getDuration(start, end) {
      const date0 = new Date(parseInt(start));
      const date1 = new Date(parseInt(end));
      const diffTime = Math.abs(date0 - date1);
      const diffMin = Math.ceil(diffTime / (1000 * 60));
      return diffMin;
    },
  },
  filters: {
    formatDateTime(dateTime) {
      try {
        const date = new Date(parseInt(dateTime));

        return new Intl.DateTimeFormat(user.language).format(dateTime);
      } catch (err) {
        console.error(err);
        return "-";
      }
    },
    replaceName(str, obj) {
      return str.replace(/({{)(.+)(}})/g, (match, p1, key) => obj[key]);
    },
  },
};
</script>

<style lang="scss">
#app {
  display: block;
}
</style>
